import { useRef, useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { TextField, Dropdown, Button, Typography, InlineAlert } from '@nwh-consumer-digital/nw-component-lib'
import { Grid, Box, Paper } from '@mui/material'
import styled from '@emotion/styled'
import { useProgress } from '../context/ProgressContext'

const Item = styled(Paper)(() => ({
  textAlign: 'left',
  maxWidth: '400px',
  marginBottom: '18px', // There is an extra 6 pixels this makes 24px
}))

const adultPageOne = (props) => {
  const { SubmissionState, onFormStateChange } = props
  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = useForm({ mode: 'onBlur' })
  //UseProgress Context Hook
  const { updateProgress } = useProgress()

  useEffect(() => {
    updateProgress(25)
    if (SubmissionState.sectionOneForm) {
      setValue('firstName', SubmissionState.sectionOneForm?.firstName)
      setValue('lastName', SubmissionState.sectionOneForm?.lastName)
      setValue('mInitial', SubmissionState.sectionOneForm?.mInitial)
      setValue('DOBChild', SubmissionState.sectionOneForm?.DOBChild)
      setValue('gender', SubmissionState.sectionOneForm?.gender)
      setValue('streetNumber', SubmissionState.sectionOneForm?.streetNumber)
      setValue('City', SubmissionState.sectionOneForm?.City)
      setValue('State', SubmissionState.sectionOneForm?.State)
      setValue('Zip', SubmissionState.sectionOneForm?.Zip)
      setValue('relationshiptoPatient', SubmissionState.sectionOneForm?.relationshiptoPatient)
    }
  }, [])

  // Count the number of errors on the page and, if any are found, scroll to the reference above the inline alert box after tallying them.
  const errorCount = Object.keys(errors).length
  const targetRef = useRef(null)
  const scrollToTarget = () => {
    if (targetRef.current) {
      targetRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }
  useEffect(() => {
    if (errorCount > 0) {
      scrollToTarget()
    }
  }, [errors])

  const handleRegistration = (data) => {
    SubmissionState.sectionOneForm = data
    SubmissionState.goBackPageTwo = true
    onFormStateChange()
  }

  return (
    <Box className="pageContainer">
      <Grid container direction="column" justifyContent="space-between" alignItems="left">
        <Grid item xs={12}>
          <Typography
            variant="h1"
            sx={{
              marginTop: {
                xs: '1.25rem',
                sm: '1.25rem',
                md: '1.25rem',
                lg: '5rem',
                xl: '5rem',
              },
              marginBottom: {
                xs: '2rem',
                sm: '2rem',
                md: '2rem',
                lg: '2rem',
                xl: '2rem',
              },
            }}>
            Información del paciente adulto
          </Typography>
        </Grid>
        <Grid
          item
          xs={12}
          style={{
            maxWidth: window.innerWidth <= 768 ? '400px' : 'none',
            marginBottom: 24,
            textAlign: 'justify',
            lineHeight: 1.6,
            wordSpacing: '0.05em',
          }}>
          <Typography variant="body" size="large" weight="plain" style={{ fontSize: 18 }}>
            Introduce los datos de la paciente adulta a continuación para quien solicitas acceso por poder.
          </Typography>
        </Grid>
        <form onSubmit={handleSubmit(handleRegistration)}>
          <Grid item xs={12} className="requiredSpacing">
            <Item elevation={0}>
              <label ref={targetRef} className="required">
                <span className="red">*</span> Requerido
              </label>
            </Item>
          </Grid>
          {errorCount > 0 && (
            <Grid item xs={12}>
              <Item elevation={0}>
                <InlineAlert severity="error" fullWidth text={`Hay errores ${errorCount} en esta página.`} />
              </Item>
            </Grid>
          )}
          <Grid item xs={12}>
            <Item elevation={0}>
              <Controller
                name="firstName"
                control={control}
                rules={{
                  required: true,
                  pattern: /^[A-Z a-z]*$/i,
                }}
                render={({ field: { onChange } }) => (
                  <TextField
                    id="firstName"
                    onChange={onChange}
                    defaultValue={SubmissionState.sectionOneForm?.firstName}
                    label={
                      <span>
                        Primer nombre del paciente <span className="red">*</span>
                      </span>
                    }
                    fullWidth
                    error={Boolean(errors?.firstName)}
                    helperText={errors?.firstName
                    ? (errors?.firstName?.type === 'required'
                      ? 'Se requiere el nombre de pila.'
                      : errors?.firstName?.type === 'pattern'
                       ? 'El nombre de pila contiene caracteres inválidos.'
                        : 'Se requiere el nombre de pila.')
                    : ''}
                  />
                )}
              />
            </Item>
          </Grid>
          <Grid item xs={12}>
            <Item elevation={0}>
              <Controller
                name="lastName"
                control={control}
                rules={{
                  required: true,
                  pattern: /^[A-Z a-z]*$/i,
                }}
                render={({ field: { onChange } }) => (
                  <TextField
                    id="lastName"
                    defaultValue={SubmissionState.sectionOneForm?.lastName}
                    onChange={onChange}
                    label={<span>Apellido del paciente <span className="red">*</span></span>}
                    fullWidth
                    error={Boolean(errors?.lastName)}
                    helperText={errors?.lastName
                      ? (errors?.lastName?.type === 'required'
                        ? 'Se requiere apellido.'
                        : errors?.lastName?.type === 'pattern'
                         ? 'El apellido contiene caracteres inválidos.'
                          : 'Se requiere apellido.')
                      : ''}
                  />
                )}
              />
            </Item>
          </Grid>
          <Grid item xs={12}>
            <Item elevation={0}>
              <Controller
                name="mInitial"
                control={control}
                rules={{
                  pattern: {
                    value: /^[A-Za-z]+$/,
                    message: 'Por favor, utiliza solo caracteres alfabéticos.',
                  },
                }}
                render={({ field: { onChange, value } }) =>
                  <TextField
                    id="mInitial"
                    onChange={onChange}
                    value={SubmissionState.sectionOneForm?.mInitial}
                    label="Inicial media"
                    fullWidth
                    error={Boolean(errors?.mInitial)}
                    helperText={errors?.mInitial?.message || ''}
                  />
                }
              />
            </Item>
          </Grid>
          <Grid item xs={12}>
            <Item elevation={0}>
              <Controller
                name="DOBChild"
                control={control}
                rules={{
                  pattern: /^(0[1-9]|1[0-2])\/(0[1-9]|[1-2][0-9]|3[0-1])\/(19\d{2}|20[0-2]\d|20[2-9][0-9]|20[3-9][0-9]|21\d{2})$/,
                  required: true,
                }}
                render={({ field: { onChange } }) => (
                  <TextField
                    id="DOBChild"
                    defaultValue={SubmissionState.sectionOneForm?.DOBChild}
                    onChange={onChange}
                    label={
                      <span>
                        Fecha de nacimiento (MM/DD/YYYY) <span className="red">*</span>
                      </span>
                    }
                    fullWidth
                    error={Boolean(errors?.DOBChild)}
                    helperText={errors?.DOBChild
                      ? (errors?.DOBChild?.type === 'required'
                        ? 'Se requiere la fecha de nacimiento.'
                        : errors?.DOBChild?.type === 'pattern'
                         ? 'Por favor, utiliza solo caracteres numéricos y corre los valores de Mes/Fecha/Año.'
                          : 'Se requiere la fecha de nacimiento.')
                      : ''}
                  />
                )}
              />
            </Item>
          </Grid>
          <Grid item xs={12}>
            <Item elevation={0}>
              <Controller
                name="gender"
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, value } }) => (
                  <Dropdown
                    id="gender"
                    label={<span>Género de nacimiento <span className="red">*</span></span>}
                    value={value}
                    onChange={onChange}
                    error={Boolean(errors?.gender)}
                   helperText={errors?.gender ? 'Se requiere el género de nacimiento.' : undefined}
                    selectOptions={[
                      { name: 'Femenino', value: 'Female' },
                      { name: 'Masculino', value: 'Male' },
                      { name: 'Otros', value: 'Other' },
                    ]}
                  />
                )}
              />
            </Item>
          </Grid>
          <Grid item xs={12}>
            <Item elevation={0}>
              <Controller
                name="streetNumber"
                control={control}
                rules={{
                  required: true,
                  pattern: /^[A-Za-z0-9 -]+$/,
                }}
                render={({ field: { onChange } }) => (
                  <TextField
                    id="streetNumber"
                    defaultValue={SubmissionState.sectionOneForm?.streetNumber}
                    onChange={onChange}
                    label={
                      <span>
                        Calle <span className="red">*</span>
                      </span>
                    }
                    fullWidth
                    error={Boolean(errors?.streetNumber)}
                    helperText={errors?.streetNumber
                      ? (errors?.streetNumber?.type === 'required'
                       ? 'Se requiere dirección.'
                        : errors?.streetNumber?.type === 'pattern'
                         ? 'Por favor, utiliza solo caracteres alfabéticos.'
                         : 'Se requiere dirección.')
                      : ''}
                  />
                )}
              />
            </Item>
          </Grid>
          <Grid item xs={12}>
            <Item elevation={0}>
              <Controller
                name="City"
                control={control}
                rules={{
                  required: true,
                  pattern: /^[A-Z a-z]*$/i,
                }}
                render={({ field: { onChange } }) => (
                  <TextField
                    id="City"
                    defaultValue={SubmissionState.sectionOneForm?.City}
                    onChange={onChange}
                    label={
                      <span>
                        Ciudad <span className="red">*</span>
                      </span>
                    }
                    fullWidth
                    error={Boolean(errors?.City)}
                    helperText={errors?.City
                      ? (errors?.City?.type === 'required'
                        ? 'La ciudad es obligatoria.'
                        : errors?.City?.type === 'pattern'
                         ? 'Por favor, utiliza solo caracteres alfabéticos.'
                          : 'La ciudad es obligatoria.')
                      : ''}
                  />
                )}
              />
            </Item>
          </Grid>
          <Grid item xs={12}>
            <Item elevation={0}>
              <Controller
                name="State"
                rules={{
                  required: true,
                  pattern: /^[A-Z a-z]*$/i,
                }}
                control={control}
                render={({ field: { onChange } }) => (
                  <TextField
                    id="State"
                    defaultValue={SubmissionState.sectionOneForm?.State}
                    onChange={onChange}
                    label={
                      <span>
                        Estado <span className="red">*</span>
                      </span>
                    }
                    fullWidth
                    error={Boolean(errors?.State)}
                    helperText={errors?.State
                      ? (errors?.State?.type === 'required'
                        ? 'Se requiere el estado.'
                        : errors?.State?.type === 'pattern'
                         ? 'Por favor, utiliza solo caracteres alfabéticos.'
                          : 'Se requiere el estado.')
                      : ''}
                  />
                )}
              />
            </Item>
          </Grid>
          <Grid item xs={12}>
            <Item elevation={0}>
              <Controller
                name="Zip"
                rules={{
                  required: true,
                  pattern: /(^\d{5}$)|(^\d{5}-\d{4}$)/,
                }}
                control={control}
                render={({ field: { onChange } }) => (
                  <TextField
                    id="Zip"
                    onChange={onChange}
                    defaultValue={SubmissionState.sectionOneForm?.Zip}
                    label={
                      <span>
                        Código postal <span className="red">*</span>
                      </span>
                    }
                    error={errors?.Zip}
                    fullWidth
                    helperText={errors?.Zip
                      ? (errors?.Zip?.type === 'required'
                       ? 'Se requiere código postal.'
                        : errors?.Zip?.type === 'pattern'
                         ? 'Por favor, utiliza solo 5 dígitos de caracteres numéricos.'
                         : 'Se requiere código postal.')
                      : ''}
                  />
                )}
              />
            </Item>
          </Grid>
          <Grid item xs={12}>
            <Item elevation={0}>
              <Controller
                name="relationshiptoPatient"
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, value } }) => (
                  <Dropdown
                    id="relationshiptoPatient"
                    value={value || ''}
                    label={
                      <span>
                        Relación con el paciente <span className="red">*</span>
                      </span>
                    }
                    error={Boolean(errors?.relationshiptoPatient)}
                    helperText={errors?.relationshiptoPatient ? 'Se requiere relación con el paciente.' : undefined}
                    selectOptions={[
                      { name: 'Padre/madre', value: 'Parent' },
                      { name: 'Cónyuge', value: 'Spouse' },
                      { name: 'Hija', value: 'Daughter' },
                      { name: 'Sonido', value: 'Son' },
                      { name: 'Tutor legal', value: 'Legal guardian' },
                      {
                        name: 'Poder Duradero (DPOA)',
                        value: 'Durable power of attorney (DPOA)',
                      },
                      { name: 'Otros', value: 'Other' },
                    ]}
                    onChange={(e) => onChange(e.target.value)}
                  />
                )}
              />
            </Item>
          </Grid>
          <Grid item xs={12}>
            <Item elevation={0}>
              <Button text="Continúa" color="primary" type="submit" />
            </Item>
          </Grid>
        </form>
      </Grid>
    </Box>
  )
}
export default adultPageOne
